/**
 * Filter utils for various shift properties.
 *
 * @namespace FilterUtils
 * @type {Object}
 */
const FilterUtils = {
	convertResourceTypesToOptions: (structures) =>
		[...new Set(structures.map((e) => e.resource_type))].map((e) => ({ id: e, label: e, value: e, checked: true })),

	convertFacilityUnitsToUnitOptions: (facilities, selectedFacilityIds) => {
		const uniqueUnitNames = [
			...new Set(
				facilities
					.filter((fc) => fc.portal_units_enabled && selectedFacilityIds[fc.id])
					.flatMap((facility) => facility.units.map((unit) => unit.unit_name).filter((_unit) => !!_unit))
			)
		];
		if (uniqueUnitNames.length) {
			return ['Unassigned', ...uniqueUnitNames].map((e) => ({ id: e, label: e, value: e, checked: true }));
		}
		return [];
	},

	convertGlobalOverviewShiftStatusToOptions: (statuses) =>
		Object.entries(statuses).map(([, status]) => ({
			id: status.name,
			label: status.name,
			value: status.name,
			checked: status.isDefault
		})),

	matchesNameOnDemandFilter: (caregiverName, caregiverFilter) => {
		const safeFilter = caregiverFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(safeFilter, 'i');
		return regex.test(caregiverName);
	},

	DEFAULT_FEATURE_OPTIONS: [
		{ label: 'Bundle', value: 'BUNDLE', checked: true },
		{ label: 'Guaranteed', value: 'GUARANTEED', checked: true },
		{ label: 'Integrated', value: 'INTEGRATED', checked: true },
		{ label: 'On Call', value: 'ON_CALL', checked: true },
		{ label: 'Orientation', value: 'ORIENTATION', checked: true },
		{ label: 'Replacement', value: 'REPLACEMENT', checked: true },
		{ label: 'None', value: 'NONE', checked: true }
	],

	DEFAULT_FLEX_FEATURE_OPTIONS: [
		{ label: 'Integrated', value: 'INTEGRATED', checked: true },
		{ label: 'Orientation', value: 'ORIENTATION', checked: true },
		{ label: 'None', value: 'NONE', checked: true }
	],

	matchShiftFeature: (shift, slot, selectedFeatures) => {
		if (
			(selectedFeatures.ORIENTATION && shift.is_orientation) ||
			(selectedFeatures.BUNDLE && !!slot.contract_id) ||
			(selectedFeatures.GUARANTEED && slot.is_guaranteed) ||
			(selectedFeatures.INTEGRATED &&
				!!slot.is_integrated &&
				(slot.caregiver_id === null || slot.has_assigned_slot_integrated)) ||
			(selectedFeatures.REPLACEMENT && slot.caregiver_replaced_name) ||
			(selectedFeatures.ON_CALL && shift.is_on_call)
		) {
			return true;
		}
		if (
			selectedFeatures.NONE &&
			!shift.is_orientation &&
			!slot.contract_id &&
			!slot.is_guaranteed &&
			!(slot.is_integrated && (slot.caregiver_id === null || slot.has_assigned_slot_integrated)) &&
			!slot.caregiver_replaced_name &&
			!shift.is_on_call
		) {
			return true;
		}
		return false;
	},

	matchShiftRoute: (shift, slot, selectedRoutes) => {
		const SHIFT_MAPPINGS = {
			ON_DEMAND: {
				type: 'ON_DEMAND',
				route: 'ShiftMed'
			},
			FLEX: {
				type: 'FLEX',
				route: 'Flex'
			},
			HYBRID: {
				route: 'Hybrid'
			}
		};

		if (
			selectedRoutes[SHIFT_MAPPINGS.ON_DEMAND.route] &&
			shift.shift_type === SHIFT_MAPPINGS.ON_DEMAND.type &&
			(!slot.is_hybrid || (slot.is_hybrid && slot.caregiver_id))
		) {
			return true;
		}
		if (selectedRoutes[SHIFT_MAPPINGS.FLEX.route] && shift.shift_type === SHIFT_MAPPINGS.FLEX.type) {
			return true;
		}
		if (selectedRoutes[SHIFT_MAPPINGS.HYBRID.route] && slot.is_hybrid && !slot.caregiver_id) {
			return true;
		}
		return false;
	},

	handleMultiSelectChange: (options, fieldName, filterOptions, filters) => {
		// here, we want to get the selected options in a flat array, by getting the ones that are checked
		// and then getting its value. After that, we use the array length to validate if all the elements
		// are selected or not.
		const selected = options.filter((option) => option.checked).map((option) => option.value);

		// we update dynamically the options array, updating the option that matches with the fieldName,
		// and then copying the rest of the elements in the object
		const updatedOptions = Object.fromEntries(
			Object.entries(filterOptions).map((option) => {
				if (option[0] === fieldName) {
					return [fieldName, options];
				}
				return option;
			})
		);

		// we update dynamically the filters array, updating the filter that matches with the fieldName name,
		// and then copying the rest of the elements in the array
		const updatedFilters = Object.fromEntries(
			Object.entries(filters).map(([key, value]) => {
				// if the selected array is empty, we set the filter to an empty array, otherwise we set it to the selected array
				return key === fieldName ? [key, selected] : [key, value];
			})
		);

		return { updatedOptions, updatedFilters };
	}
};

export default FilterUtils;
